/**
 * DraftBatch — inline review of the vision agent's pending drafts (Fase 6).
 *
 * Rendered in the assistant thread once the photo turn has proposed drafts.
 * Self-hides when there are no pending drafts. Each draft is an editable
 * <DraftReviewCard>; the sticky footer saves the batch with the parent's
 * reviewed payload (including explicit meal overrides) or discards it.
 *
 * #158 (steg 8b, Stell_Utkast): kompakte kort uten egen ramme rundt, og
 * «Lagre alle N» med hengelås (bekreftelsen krever passord) i en klebrig
 * bunn som blir stående nederst i tråden mens kortene rulles. Knappen er
 * ugjennomsiktig og har ingen flate bak seg.
 *
 * D13 (#163): «Lagre N av M» lagrer alt som er lov og lar resten ligge.
 *   - En middag med allergenadvarsel uten «Jeg tar ansvar» sendes ikke, og
 *     knappen teller den ikke («Lagre 2 av 3»). Serveren er likevel oraklet:
 *     den skanner hvert utkast på nytt og hopper over en middag med treff
 *     uten override (`skipped: allergen_blocked`), også en klienten trodde
 *     var ren. Blokken er fail-closed på begge sider.
 *   - Et ugyldig utkast sendes ikke; skjemaet dets åpnes.
 *   - Det som ble liggende, står igjen med advarselen sin, og en melding sier
 *     «Lagret N av M» og hvilke kort som ble liggende, og hvorfor.
 *   «Forkast alle» forkaster resten av batchen (utkastene forelderen ser).
 *
 * Confirm/discard are requireAdminScope-gated, so a 403 admin-scope error opens
 * <AdminReauthModal> and retries — the same runGuarded pattern as
 * ChoreTemplateAdminSection.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreateCalendarEventDraftInputSchema,
  CreateChoreDraftInputSchema,
  CreateMealDraftInputSchema,
  type AiDraftDTO,
  type ConfirmDraftBatchItem,
} from '@stell/shared';
import { useAdminMembers, ApiError, isAdminScopeError } from '../admin/useMembers';
import { AdminReauthModal } from '../admin/AdminReauthModal';
import { useShoppingList } from '../shopping/useShoppingList';
import { Icon } from '../../components/primitives';
import { Button } from '../../components/primitives/Button';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { DraftReviewCard } from './DraftReviewCard';
import {
  useDrafts,
  useConfirmDraft,
  useConfirmDrafts,
  useDiscardDraft,
  useDiscardDrafts,
  type DraftPayload,
} from './useVisionDrafts';

/** Tittelen slik forelderen ser den (redigert eller foreslått). */
function draftTitleFrom(payload: unknown): string {
  const title = (payload as { title?: unknown } | null)?.title;
  return typeof title === 'string' ? title.trim() : '';
}

/** Grunnen til at et utkast ble liggende etter «Lagre N av M». */
type KeptReason = 'allergen_blocked' | 'invalid_input' | 'other';
const KEPT_REASONS: KeptReason[] = ['allergen_blocked', 'invalid_input', 'other'];

interface BatchReport {
  saved: number;
  total: number;
  kept: Array<{ id: string; title: string; reason: KeptReason }>;
}

/** null: utkastet er avgjort eller borte (f.eks. lagret på en annen enhet), ikke liggende. */
function keptReason(code: string): KeptReason | null {
  if (code === 'draft_not_pending' || code === 'not_found') return null;
  return code === 'allergen_blocked' || code === 'invalid_input' ? code : 'other';
}

/** En middag kortet viser som sperret: advarsel og ingen «Jeg tar ansvar». */
function isBlockedMeal(d: AiDraftDTO, overridden: boolean): boolean {
  return d.kind === 'meal' && Boolean(d.allergen_warning && d.allergen_warning.length > 0) && !overridden;
}

const titleList = new Intl.ListFormat('nb', { type: 'conjunction' });

interface DraftBatchProps {
  conversationId: string | undefined;
}

export function DraftBatch({ conversationId }: DraftBatchProps) {
  const { t } = useTranslation();
  const { data: drafts } = useDrafts(conversationId);
  const { data: members } = useAdminMembers();
  const { data: shoppingItems } = useShoppingList();
  const shoppingTexts = (shoppingItems ?? []).map((i) => i.text);
  const confirmMut = useConfirmDraft(conversationId);
  const discardMut = useDiscardDraft(conversationId);
  const confirmBatchMut = useConfirmDrafts(conversationId);
  const discardBatchMut = useDiscardDrafts(conversationId);
  const [error, setError] = useState<string | null>(null);
  // Hva «Lagre N av M» lot ligge (#163). Skjemaene er foldet sammen, så
  // meldingen må si hvilke kort det gjelder, og et ugyldig kort åpnes.
  const [report, setReport] = useState<BatchReport | null>(null);
  // Middagene med «Jeg tar ansvar» krysset av. State, ikke bare ref: knappen
  // teller dem («Lagre 2 av 3» → «Lagre alle 3»).
  const [overridden, setOverridden] = useState<ReadonlySet<string>>(() => new Set());
  // Den redigerte nyttelasten og avkrysningen per utkast, lest når lagringen
  // faktisk kjører: et nytt forsøk etter passordet skal bruke det forelderen
  // har krysset av NÅ, ikke det som gjaldt ved første trykk.
  const reviewedDraftsRef = useRef<Record<string, { payload: DraftPayload; override: boolean }>>({});
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({ open: false, retry: null });
  const headingId = useId();

  const pending = (drafts ?? []).filter((d) => d.status === 'pending');

  useEffect(() => {
    const activeIds = new Set(pending.map((d) => d.id));
    for (const id of Object.keys(reviewedDraftsRef.current)) {
      if (!activeIds.has(id)) delete reviewedDraftsRef.current[id];
    }
    setOverridden((prev) => {
      if ([...prev].every((id) => activeIds.has(id))) return prev;
      return new Set([...prev].filter((id) => activeIds.has(id)));
    });
  }, [pending]);

  // Over den tidlige returen (#158): sto den under, ble det én krok mer når
  // utkastene kom etter første tegning (tom liste → kort), og React kastet
  // #310 («Rendered more hooks») — hele assistenten falt i feilgrensen.
  const handleReviewChange = useCallback((draftId: string, payload: DraftPayload, opts?: { override?: boolean }) => {
    const override = opts?.override === true;
    reviewedDraftsRef.current[draftId] = { payload, override };
    setOverridden((prev) => {
      if (prev.has(draftId) === override) return prev;
      const next = new Set(prev);
      if (override) next.add(draftId);
      else next.delete(draftId);
      return next;
    });
  }, []);

  if (pending.length === 0) return null;

  async function runGuarded(fn: () => Promise<void>): Promise<void> {
    setError(null);
    setReport(null);
    try {
      await fn();
    } catch (err) {
      if (isAdminScopeError(err)) {
        setReauth({ open: true, retry: () => void runGuarded(fn) });
        return;
      }
      setError(err instanceof ApiError ? err.code : 'unknown');
    }
  }

  function confirmOne(draft: AiDraftDTO, payload: DraftPayload, opts?: { override?: boolean }): void {
    void runGuarded(async () => {
      await confirmMut.mutateAsync({ id: draft.id, payload, ...(opts?.override ? { override: true } : {}) });
    });
  }

  function discardOne(draft: AiDraftDTO): void {
    void runGuarded(async () => {
      await discardMut.mutateAsync(draft.id);
    });
  }

  function confirmAll(): void {
    const batch = pending;
    void runGuarded(async () => {
      const reasons = new Map<string, KeptReason>();
      const items: ConfirmDraftBatchItem[] = [];
      for (const d of batch) {
        const reviewed = reviewedDraftsRef.current[d.id];
        const payload = reviewed?.payload ?? (d.payload as DraftPayload);
        const override = reviewed?.override === true;
        const parsed =
          d.kind === 'event'
            ? CreateCalendarEventDraftInputSchema.safeParse(payload)
            : d.kind === 'meal'
              ? CreateMealDraftInputSchema.safeParse(payload)
              : CreateChoreDraftInputSchema.safeParse(payload);
        if (!parsed.success) {
          reasons.set(d.id, 'invalid_input');
          continue;
        }
        // Fail-closed i klienten: en sperret middag sendes ikke i det hele tatt.
        if (isBlockedMeal(d, override)) {
          reasons.set(d.id, 'allergen_blocked');
          continue;
        }
        items.push({ id: d.id, payload: parsed.data, ...(override ? { override: true } : {}) });
      }

      let saved = 0;
      if (items.length > 0) {
        const result = await confirmBatchMut.mutateAsync(items);
        saved = result.done.length;
        for (const s of result.skipped) {
          const reason = keptReason(s.error);
          if (reason) reasons.set(s.id, reason);
        }
      }

      if (reasons.size === 0) return; // alt er lagret; batchen forsvinner
      setReport({
        saved,
        total: batch.length,
        kept: batch
          .filter((d) => reasons.has(d.id))
          .map((d) => ({
            id: d.id,
            title: draftTitleFrom(reviewedDraftsRef.current[d.id]?.payload ?? d.payload),
            reason: reasons.get(d.id)!,
          })),
      });
    });
  }

  function discardAll(): void {
    const ids = pending.map((d) => d.id);
    void runGuarded(async () => {
      await discardBatchMut.mutateAsync(ids);
    });
  }

  const busy = confirmMut.isPending || discardMut.isPending || confirmBatchMut.isPending || discardBatchMut.isPending;
  const blockedCount = pending.filter((d) => isBlockedMeal(d, overridden.has(d.id))).length;
  const savable = pending.length - blockedCount;
  const saveLabel =
    blockedCount === 0
      ? t('assistant.vision.confirmAll', { count: pending.length })
      : t('assistant.vision.confirmSome', { saved: savable, count: pending.length });
  const needsAttention = new Set(report?.kept.filter((k) => k.reason === 'invalid_input').map((k) => k.id));

  return (
    <section
      data-testid="draft-batch"
      aria-labelledby={headingId}
      className="mt-4 flex flex-col gap-2.5"
    >
      <h2 id={headingId} className="m-0 px-1 text-sm font-extrabold text-ink-2">
        {t('assistant.vision.batchTitle', { count: pending.length })}
      </h2>

      {error && (
        <p
          role="alert"
          className={`${GLASS_SURFACE} m-0 rounded-md px-3.5 py-2.5 text-sm font-bold text-bad`}
          data-testid="draft-batch-error"
        >
          {t('assistant.vision.actionError')}
        </p>
      )}

      {report && (
        <div
          role="status"
          className={`${GLASS_SURFACE} flex flex-col gap-1 rounded-md px-3.5 py-2.5 text-sm font-bold text-ink`}
          data-testid="draft-batch-report"
        >
          <p className="m-0">{t('assistant.vision.savedOf', { saved: report.saved, count: report.total })}</p>
          {KEPT_REASONS.map((reason) => {
            const titles = report.kept
              .filter((k) => k.reason === reason)
              .map((k) => `«${k.title || t('assistant.vision.untitled')}»`);
            if (titles.length === 0) return null;
            return (
              <p
                key={reason}
                className={`m-0 ${reason === 'allergen_blocked' ? 'text-bad' : ''}`}
                data-testid={`draft-batch-kept-${reason}`}
              >
                {t(`assistant.vision.kept_${reason}`, { titles: titleList.format(titles), count: titles.length })}
              </p>
            );
          })}
        </div>
      )}

      {pending.map((d) => (
        <DraftReviewCard
          key={d.id}
          draft={d}
          members={members ?? []}
          busy={busy}
          shoppingTexts={shoppingTexts}
          onReviewChange={handleReviewChange}
          onConfirm={(payload, opts) => confirmOne(d, payload, opts)}
          onDiscard={() => discardOne(d)}
          needsAttention={needsAttention.has(d.id)}
        />
      ))}

      {/* Knappene flyter over kortene uten egen flate, som i skissen; en
          tonet stripe bak dem ble et nytt flatt bånd over gløden. «Forkast
          alle» er glass med uskarphet, så kortene under ikke skinner gjennom,
          og tar bredden teksten trenger; lagreknappen fyller resten. */}
      <div className="sticky bottom-0 flex gap-2.5 pt-2 pb-1">
        <Button
          variant="secondary"
          size={56}
          onClick={discardAll}
          disabled={busy}
          data-testid="draft-discard-all"
          className="shrink-0 backdrop-blur-card"
        >
          {t('assistant.vision.discardAll')}
        </Button>
        <Button
          variant="primary"
          size={56}
          onClick={confirmAll}
          // Ikke deaktivert ved «Lagre 0 av M»: knappen er da gjennomsiktig
          // over kortene. Et trykk sender ingenting og melder hvorfor.
          disabled={busy}
          data-testid="draft-confirm-all"
          className="min-w-0 flex-1"
        >
          <Icon.Lock size={18} />
          {saveLabel}
        </Button>
      </div>

      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={() => {
          const retry = reauth.retry;
          setReauth({ open: false, retry: null });
          retry?.();
        }}
      />
    </section>
  );
}
